import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Input,
  Typography,
  Space,
  Spin,
  message,
  Tag,
  Drawer,
  List,
  Tooltip,
  Popconfirm,
} from 'antd';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  HistoryOutlined,
  RollbackOutlined,
  EyeOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import CodeMirror from '@uiw/react-codemirror';
import { xml } from '@codemirror/lang-xml';
import { oneDark } from '@codemirror/theme-one-dark';
import axios from 'axios';

const { Title, Text } = Typography;

interface Prompt {
  id: number;
  name: string;
  content: string;
  char_count: number;
  version_count: number;
  created_at: string;
}

interface PromptVersion {
  id: number;
  version: number;
  char_count: number;
  content?: string;
  created_at: string;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

const PromptEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [versionCount, setVersionCount] = useState(0);

  // Version history drawer
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState<PromptVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [previewVersion, setPreviewVersion] = useState<PromptVersion | null>(null);

  useEffect(() => {
    if (!isNew && id) {
      fetchPrompt();
    }
  }, [id, isNew]);

  const fetchPrompt = async () => {
    try {
      const response = await axios.get(`${API_URL}/prompts`);
      const prompt = response.data.find((p: Prompt) => p.id === parseInt(id!));
      if (prompt) {
        setName(prompt.name);
        setContent(prompt.content);
        setOriginalContent(prompt.content);
        setVersionCount(prompt.version_count || 1);
      } else {
        message.error('Prompt not found');
        navigate('/prompts');
      }
    } catch (error) {
      console.error('Failed to fetch prompt:', error);
      message.error('Failed to load prompt');
      navigate('/prompts');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      message.error('Please enter a prompt name');
      return;
    }
    if (!content.trim()) {
      message.error('Please enter prompt content');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        await axios.post(`${API_URL}/prompts`, { name, content });
        message.success('Prompt created successfully');
      } else {
        await axios.put(`${API_URL}/prompts/${id}`, { name, content });
        message.success('Prompt saved successfully');
        setOriginalContent(content);
        setVersionCount(prev => prev + 1);
      }
      navigate('/prompts');
    } catch (error) {
      console.error('Failed to save prompt:', error);
      message.error('Failed to save prompt');
    } finally {
      setSaving(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setLoadingVersions(true);
    setPreviewVersion(null);
    try {
      const response = await axios.get(`${API_URL}/prompts/${id}/versions`);
      setVersions(response.data);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
      message.error('Failed to load version history');
    } finally {
      setLoadingVersions(false);
    }
  };

  const previewVersionContent = async (version: PromptVersion) => {
    try {
      const response = await axios.get(`${API_URL}/prompts/${id}/versions/${version.version}`);
      setPreviewVersion(response.data);
    } catch (error) {
      console.error('Failed to fetch version content:', error);
      message.error('Failed to load version content');
    }
  };

  const revertToVersion = async (version: number) => {
    try {
      const response = await axios.post(`${API_URL}/prompts/${id}/revert/${version}`);
      setContent(response.data.content);
      setOriginalContent(response.data.content);
      setVersionCount(response.data.version_count);
      message.success(`Reverted to version ${version}`);
      setHistoryOpen(false);
      // Refresh versions
      const versionsResponse = await axios.get(`${API_URL}/prompts/${id}/versions`);
      setVersions(versionsResponse.data);
    } catch (error) {
      console.error('Failed to revert version:', error);
      message.error('Failed to revert to version');
    }
  };

  const loadVersionIntoEditor = () => {
    if (previewVersion) {
      setContent(previewVersion.content || '');
      setPreviewVersion(null);
      message.info('Version loaded into editor. Save to keep changes.');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const hasChanges = content !== originalContent;

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/prompts')}
            >
              Back
            </Button>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Prompt name"
              style={{ width: '300px', fontSize: '18px', fontWeight: 'bold' }}
              size="large"
            />
          </div>
          <Space>
            <Tag color="blue">{content.length.toLocaleString()} characters</Tag>
            {!isNew && (
              <Tag color="purple">{versionCount} version{versionCount !== 1 ? 's' : ''}</Tag>
            )}
            {hasChanges && <Tag color="orange">Unsaved changes</Tag>}
            {!isNew && (
              <Button icon={<HistoryOutlined />} onClick={openHistory}>
                History
              </Button>
            )}
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={saving}
            >
              {isNew ? 'Create' : 'Save'}
            </Button>
          </Space>
        </div>
      </Card>

      {/* Editor */}
      <Card
        style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
        bodyStyle={{ flex: 1, padding: 0, display: 'flex', flexDirection: 'column' }}
      >
        <CodeMirror
          value={content}
          onChange={(value) => setContent(value)}
          extensions={[xml()]}
          theme={oneDark}
          style={{ flex: 1, overflow: 'auto' }}
          height="100%"
          basicSetup={{
            lineNumbers: true,
            highlightActiveLineGutter: true,
            highlightSpecialChars: true,
            foldGutter: true,
            drawSelection: true,
            dropCursor: true,
            allowMultipleSelections: true,
            indentOnInput: true,
            syntaxHighlighting: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: true,
            rectangularSelection: true,
            crosshairCursor: true,
            highlightActiveLine: true,
            highlightSelectionMatches: true,
            closeBracketsKeymap: true,
            defaultKeymap: true,
            searchKeymap: true,
            historyKeymap: true,
            foldKeymap: true,
            completionKeymap: true,
            lintKeymap: true,
          }}
        />
      </Card>

      {/* Version History Drawer */}
      <Drawer
        title={
          <Space>
            <HistoryOutlined />
            Version History
          </Space>
        }
        placement="right"
        width={previewVersion ? 800 : 400}
        onClose={() => {
          setHistoryOpen(false);
          setPreviewVersion(null);
        }}
        open={historyOpen}
      >
        <div style={{ display: 'flex', height: '100%' }}>
          {/* Version List */}
          <div style={{ width: previewVersion ? '300px' : '100%', borderRight: previewVersion ? '1px solid #f0f0f0' : 'none', paddingRight: previewVersion ? '16px' : 0 }}>
            {loadingVersions ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin />
              </div>
            ) : (
              <List
                dataSource={versions}
                renderItem={(version, index) => (
                  <List.Item
                    style={{
                      backgroundColor: previewVersion?.version === version.version ? '#f0f5ff' : undefined,
                      padding: '12px',
                      marginBottom: '8px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                    }}
                    onClick={() => previewVersionContent(version)}
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          <Text strong>Version {version.version}</Text>
                          {index === 0 && <Tag color="green">Current</Tag>}
                        </Space>
                      }
                      description={
                        <div>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {version.char_count.toLocaleString()} chars
                          </Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            {formatDate(version.created_at)}
                          </Text>
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </div>

          {/* Version Preview */}
          {previewVersion && (
            <div style={{ flex: 1, paddingLeft: '16px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Title level={5} style={{ margin: 0 }}>
                  Version {previewVersion.version} Preview
                </Title>
                <Space>
                  <Button onClick={loadVersionIntoEditor}>
                    Load into Editor
                  </Button>
                  {versions.findIndex(v => v.version === previewVersion.version) > 0 && (
                    <Popconfirm
                      title="Revert to this version?"
                      description="This will create a new version with this content."
                      onConfirm={() => revertToVersion(previewVersion.version)}
                      okText="Revert"
                      cancelText="Cancel"
                    >
                      <Button type="primary" icon={<RollbackOutlined />}>
                        Revert
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              </div>
              <div
                style={{
                  flex: 1,
                  backgroundColor: '#282c34',
                  borderRadius: '8px',
                  overflow: 'auto',
                }}
              >
                <CodeMirror
                  value={previewVersion.content || ''}
                  extensions={[xml()]}
                  theme={oneDark}
                  editable={false}
                  height="100%"
                  basicSetup={{
                    lineNumbers: true,
                    foldGutter: true,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default PromptEdit;
